import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Maximize2Icon } from "lucide-react";
import { useMemo, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../ui/dialog";

import ImageCarousel from "./ImageCarousel";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { cn } from "@/lib/utils";

interface IImagePreviewerProps {
  imagesSrc: string | string[];
  alt?: string;
  classes?: {
    imgClassName?: string;
    containerClassName?: string;
  };
}

interface IWithTrigger {
  trigger: JSX.Element;
  imageCount?: never;
}
interface IWithoutTrigger {
  trigger?: never;
  imageCount: number;
}
interface IShowHoverContent {
  showDialogOnly?: never;
  showHoverContent?: boolean;
  side?: "left" | "right" | "top" | "bottom";
}
interface IShowDialogOnly {
  showDialogOnly: boolean;
  showHoverContent?: never;
  side?: never;
}

export const isPdf = (src: string): boolean => src.endsWith(".pdf");

export const ImagePreviewer: React.FC<
  IImagePreviewerProps &
    (IShowDialogOnly | IShowHoverContent) &
    (IWithTrigger | IWithoutTrigger)
> = ({
  trigger,
  imagesSrc,
  alt = "Image",
  imageCount = 4,
  showHoverContent = false,
  classes,
  showDialogOnly = !showHoverContent,
  side = "bottom",
}) => {
  const srcArray = useMemo(
    () => (Array.isArray(imagesSrc) ? imagesSrc : [imagesSrc]),
    [imagesSrc],
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [displayImage, setDisplayImage] = useState<string | null>(null);

  const handleNext = (): void => {
    setCurrentIndex((prev) => (prev === srcArray?.length - 1 ? 0 : prev + 1));
  };

  const handlePrevious = (): void => {
    setCurrentIndex((prev) => (prev === 0 ? srcArray?.length - 1 : prev - 1));
  };

  const isValidSrc = srcArray[0]?.startsWith("https");

  return (
    <div
      onClick={(event) => event.stopPropagation()}
      onContextMenu={(event) => {
        event.stopPropagation();
      }}
    >
      <HoverCard>
        {trigger ? (
          <HoverCardTrigger
            onMouseOver={() => {
              setIsDialogOpen(false);
            }}
            onClick={() => {
              if (showDialogOnly) {
                setIsDialogOpen(true);
              }
            }}
            className='cursor-pointer'
          >
            {trigger}
          </HoverCardTrigger>
        ) : (
          <div className={cn("flex gap-2", classes?.containerClassName)}>
            {srcArray?.slice(0, imageCount).map((image, index) => (
              <HoverCardTrigger
                asChild
                onMouseOver={() => {
                  setDisplayImage(image);
                  setCurrentIndex(index);
                  if (showHoverContent) {
                    setIsDialogOpen(false);
                  }
                }}
                onClick={() => {
                  if (showDialogOnly) {
                    setIsDialogOpen(true);
                  }
                }}
                key={index}
              >
                <img
                  key={index}
                  src={image}
                  alt='image'
                  className={cn(
                    "object-fill w-8 h-8 rounded-sm cursor-pointer",
                    classes?.imgClassName,
                  )}
                />
              </HoverCardTrigger>
            ))}

            {srcArray?.length > imageCount && (
              <HoverCardTrigger
                asChild
                onMouseOver={() => {
                  setDisplayImage(imagesSrc[imageCount]);
                  setCurrentIndex(imageCount);
                  if (showHoverContent) {
                    setIsDialogOpen(false);
                  }
                }}
                onClick={() => {
                  if (showDialogOnly) {
                    setIsDialogOpen(true);
                  }
                }}
              >
                <span
                  className={cn(
                    "flex items-center justify-center w-8 h-8 text-xs text-gray-600 bg-gray-200 rounded-sm cursor-pointer",
                    classes?.imgClassName,
                  )}
                >
                  +{srcArray?.length - imageCount}
                </span>
              </HoverCardTrigger>
            )}
          </div>
        )}

        {srcArray?.length > 0 &&
          showHoverContent &&
          !showDialogOnly &&
          isValidSrc && (
            <HoverCardContent
              className={cn(
                "w-64 h-64",
                !isValidSrc && "hidden",
                isDialogOpen && "hidden",
              )}
              side={side}
            >
              <div className={"w-full h-full"}>
                <img
                  src={displayImage || srcArray[currentIndex]}
                  alt={alt}
                  className='object-cover h-60 w-60'
                />

                <div
                  className='absolute p-2 transition-all rounded-lg shadow-xl cursor-pointer bg-background -right-1 -top-1 hover:opacity-100 hover:top-0'
                  onClick={() => setIsDialogOpen(true)}
                >
                  <Maximize2Icon size={12} />
                </div>
              </div>
            </HoverCardContent>
          )}
      </HoverCard>
      <Dialog open={isDialogOpen} onOpenChange={() => setIsDialogOpen(false)}>
        <VisuallyHidden>
          <DialogTitle />
          <DialogDescription />
        </VisuallyHidden>
        {isValidSrc && (
          <DialogContent className='max-w-4xl p-0 bg-black/60 backdrop-blur-sm z-[9999] border-none'>
            <ImageCarousel
              srcArray={srcArray}
              handleNext={handleNext}
              handlePrevious={handlePrevious}
              currentIndex={currentIndex}
            />
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
};
